<template>
  <div class="common-header">
    <div :class="{ header__grid: isAccount }" class="header">
      <div class="header__menu grid-child__first-row">
        <router-link class="header__logo" to="/">
          <img alt="logo" height="40" src="@/assets/logo/logo.png" width="40" />
        </router-link>
        <a
          :href="isL2 ? 'https://lumio.io/' : 'https://home.liquidswap.com/'"
          class="learn-button"
          rel="noreferrer noopenner"
          target="_blank"
          @click="onClickLearnMore"
        >
          <img
            alt=""
            class="learn-button__icon"
            height="18"
            src="@/assets/info.svg"
            width="18"
          />
          {{ $t('header.learnMore') }}
        </a>
      </div>
      <app-links class="header__links grid-child__first-row" />
      <div class="header__actions grid-child__first-row">
        <PButton
          v-if="haveBuyAptUrls"
          class="p-button-rounded ml-2 moonpay-button"
          @click="toggleBuyAptMenu"
        >
          <img height="24" src="@/assets/tokens/apt.svg" width="24" />
          <span class="ml-2">{{ $t('header.buyAPT') }}</span>
        </PButton>
        <p-menu
          ref="menu"
          :model="buyAptMenuItems"
          :popup="true"
          class="buy-apt-container"
        >
          <template #item="{ item }">
            <BuyAptCard
              :description="item.description"
              :fee="item.fee"
              :img-path="item.targetImg"
              :is-link="item.type === 'moonpay' || item.type === 'coinbase'"
              :target-url="item.url"
              :title="item.title"
              @click="onBuyAptClick($event, item.type)"
            />
          </template>
        </p-menu>

        <p-button
          class="mx-2 p-button p-component p-button-icon-only p-button-rounded social-button btn-notifi"
          @click="showNotifiCard"
        >
          <div class="social-button__icon social-button__icon_type_bell" />
        </p-button>
        <p-button
          v-if="!isAccount"
          class="wallet-button p-button-primary"
          label="Connect Wallet"
          @click="onConnectWallet"
        />
        <template v-else>
          <template v-if="isTestnet || isL2">
            <p-button
              class="account__button faucet-button mx-2"
              @click="onRequestTokens"
            >
              <span v-if="isL2">{{ $t('header.faucet') }}</span>
              <span v-else>{{ $t('header.aptosFaucet') }}</span>
              <div class="faucet-button__inner" />
            </p-button>
          </template>
          <div class="referral-id">
            <request-tokens class="header__request" />
            <ReferralId v-if="isReferralBlockDisplay" />
          </div>
        </template>
        <NetworkSwitch
          :is-lumio="isL2"
          :correct-chain-id="CORRECT_CHAIN.chainId"
        />
        <social-menu />
        <mobile-menu />
      </div>
    </div>
    <div class="developed">
      <div class="developed__img-wrapper">
        <img src="@/assets/logo/logo-white.svg" />
      </div>
      <span> {{ $t('header.developed') }} </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  AppLinks,
  MobileMenu,
  RequestTokens,
  SocialMenu,
} from '@/components/CommonHeader';
import { computed, inject, ref, onMounted, watch } from 'vue';
import { ChainsEnum, CORRECT_CHAIN } from '@/constants';
import BuyAptCard from '@/components/CommonHeader/BuyAptCard.vue';
import { useStore } from '@/store';
import { useDialog } from 'primevue/usedialog';
import {
  buyAptMenuItems,
  handlePurchaseByTransak,
  haveBuyAptUrls,
  TPaymentBrand,
} from '@/composables/usePaymentMenuService';
import {
  showInstallMobileAppDialogByOS,
  showRequestTokenDialog,
} from '@/composables/useDialogService';
import { EMixpanelEventNames, EMixpanelProvideKeys } from '@/types/enums';
import { ReferralId } from '@/components/CommonHeader/ReferralId';
import { useMediaQuery } from '@vueuse/core';
import { NetworkSwitch } from 'ls-ui';
import { useWalletPlugin } from '@/composables/useWalletPlugin';

const track: any = inject(EMixpanelProvideKeys.Track);

const autoConnect = ref(false);
const dialog = useDialog();
const { account, connect } = useWalletPlugin();
const isAccount = computed(() => !!account.value?.address);
const mainStore = useStore();
const isL2 = computed(() => CORRECT_CHAIN.id === ChainsEnum['lumio-testnet']);
const isMovement = computed(() =>
  [ChainsEnum['movement-testnet'], ChainsEnum['movement-mainnet']].includes(
    CORRECT_CHAIN.id,
  ),
);

const isReferralBlockDisplay = computed(
  () =>
    mainStore.account.value?.address &&
    useMediaQuery('(min-width: 1401px)').value &&
    !isMovement.value,
);

/**
 * Buy APT Menu ref
 */
const menu = ref();
const toggleBuyAptMenu = (event: MouseEvent) => {
  const willOverlayOpen = menu.value.overlayVisible === false;
  if (willOverlayOpen) track(EMixpanelEventNames.BuyAptClicked);

  menu.value.toggle(event);
};

function onConnectWallet() {
  showInstallMobileAppDialogByOS(dialog);
}

function showNotifiCard() {
  mainStore.showDialog('notifiCard');
}

function onBuyAptClick(event: MouseEvent, type: TPaymentBrand) {
  toggleBuyAptMenu(event);

  if (type === 'moonpay') {
    track(EMixpanelEventNames.StartMoonpay);
    return;
  }
  if (type === 'coinbase') {
    track(EMixpanelEventNames.StartCoinbase);
    return;
  }

  if (type === 'transak') {
    track(EMixpanelEventNames.StartTransak);
    handlePurchaseByTransak(account.value.address as string, track);
  }
}

async function onRequestTokens() {
  track(EMixpanelEventNames.FaucetClick);
  showRequestTokenDialog(dialog);
}

function onClickLearnMore() {
  track(EMixpanelEventNames.LearnMoreClicked);
}

const isTestnet = computed(() => {
  return CORRECT_CHAIN.id === ChainsEnum['aptos-testnet'];
});

const LOCAL_STORAGE_KEY = 'AptosWalletName';
watch(
  autoConnect,
  async (newAutoConnect) => {
    if (newAutoConnect) {
      if (localStorage.getItem(LOCAL_STORAGE_KEY) && !account.value) {
        try {
          await connect(localStorage.getItem(LOCAL_STORAGE_KEY));
        } catch (error) {
          console.log('error connect', error);
        }
      }
    }
  },
  {
    immediate: true,
  },
);

onMounted(async () => {
  await new Promise((r) => setTimeout(r, 300));
  autoConnect.value = true;
});
</script>

<style lang="scss">
$hide-header: 1400px;

.buy-apt-container.p-menu {
  z-index: 1;

  box-sizing: content-box;
  width: 360px;
  padding: 0.5rem;

  border-radius: 0.75rem;
  background-color: #28253e;
}

.p-button.social-button {
  width: 40px;
  height: 40px !important;
  border: none;

  color: var(--gray-300);

  background-color: #14142f;
  transition: color, background-color 0.15s ease;

  &:enabled:hover,
  &:enabled:active,
  &:enabled:focus,
  &:enabled:focus-visible {
    background-color: #3e3e52;
    color: var(--primary-color-text);
  }

  &:enabled:focus {
    box-shadow: none;
  }

  &.mobile-menu {
    display: none !important;

    @media screen and (max-width: 1400px) {
      display: flex !important;
    }
  }

  @media screen and (max-width: 1400px) {
    display: none !important;
  }
}

.social-button__icon {
  width: 100%;
  height: 100%;

  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
  background-color: #d4c4ed;

  transition: background-color 0.15s ease;
  will-change: background-color;

  &_type_bell {
    mask-image: url('@/assets/notifi-bell-icon.svg');
  }

  &_type_ellipsis {
    height: 1.5rem;

    mask-image: url('@/assets/ellipsis.svg');
  }

  .social-button:hover &,
  .social-button:active &,
  .social-button:focus &,
  .social-button:focus-visible & {
    background-color: var(--primary-color-text);
  }
}

@media screen and (max-width: $hide-header) {
  .common-header .faucet-button {
    display: none;
  }
}
</style>
<style lang="scss" scoped>
$hide-header: 1400px;

.header {
  position: relative;

  display: flex;
  align-items: center;
  height: 4.25rem;
  padding: 0.75rem 1rem;

  &__logo {
    border-radius: 10px;
    outline: none;
  }

  &__logo:focus,
  &__logo:focus-visible {
    box-shadow: 0 0 0 2px #1c2127, 0 0 0 4px rgba(196, 181, 253, 0.7),
      0 1px 2px 0 rgba(0, 0, 0, 0);
  }

  &__actions {
    display: flex;
    justify-content: flex-end;

    height: 40px;
    margin-left: auto;
  }

  @media screen and (max-width: 767px) {
    padding: 0.75rem;

    &__grid {
      display: flex;
    }
  }
}

.header__menu {
  display: flex;
  height: 40px;

  a {
    display: block;
  }

  .learn-button {
    display: flex;
    justify-content: flex-start;
    align-items: center;

    margin: 0 0 0 0.5rem;
    padding: 0.5rem 0.5rem 0.5rem 0.25rem;

    outline: none;
    border-radius: 8px;
    transition: background-color 0.15s ease;
    will-change: background-color, color;

    font-size: 0.9375rem;
    font-weight: 500;
    font-family: var(--font-family);
    color: #e5e4fa;

    &:hover,
    &:focus,
    &:focus-visible {
      background-color: #3e3e52;
      color: #fff;
    }

    @media screen and (max-width: 1400px) {
      display: none;
    }

    &__icon {
      margin-right: 0.25rem;
    }
  }

  @media screen and (max-width: 767px) {
    img {
      height: 40px;
      width: 40px;
    }
  }
}

.header__links {
  margin-left: 1rem;

  @media screen and (max-width: 1307px) {
    margin-left: 0;
  }
}

.wallet-button {
  display: flex;
  align-items: center;
  justify-content: center;

  height: 40px;
  line-height: 1.125rem;

  @media screen and (max-width: 767px) {
    margin-left: 12px;
    padding: 0 1rem;
    border-radius: 70px;
    font-size: 0.9325rem;
  }
}

.account {
  height: 40px;
}

.account__button {
  display: flex;
  flex-flow: row nowrap;
  border: 1px solid var(--surface-card);
  background-color: #24243a;
  color: rgb(255, 255, 255);
  font-weight: 500;
  padding: 0.5rem 0.8rem;
  border-radius: 14px;
  height: 36px;
  align-items: center;
  cursor: pointer;
  user-select: none;

  @media screen and (max-width: 767px) {
    align-items: center;
    height: 40px;
    padding-left: 1rem;
    margin: 0;
    border-radius: 51px;
    font-size: 0.9325rem;
    font-weight: 500;
  }
}

.faucet-button {
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  background: linear-gradient(-45deg, #ffa63d, #ff3d77, #338aff, #3cf0c5);
  background-size: 600%;
  animation: anime 16s linear infinite;
  border-color: transparent !important;

  &:hover {
    box-shadow: 0 0 0 2px #1c2127, 0 0 0 4px rgb(196 181 253 / 70%),
      0 1px 2px 0 rgb(0 0 0 / 0%) !important;
  }

  &__inner {
    position: absolute;
    margin-top: -70px;
    z-index: -1;
    filter: blur(30px);
    opacity: 0.8;
  }

  @media screen and (max-width: 767px) {
    margin: 0 !important;
  }
}

.grid-child {
  &__first-row {
    @media screen and (max-width: 1199px) {
      grid-row: 1/2;
    }
  }
}

@keyframes anime {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

.developed {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;

  span {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 2.4px;
    opacity: 0.6;
    margin-left: 12px;
    line-height: 16px;
    margin-bottom: 4px;
  }

  img {
    width: 24px;
    height: 24px;
    opacity: 0.6;
  }

  @media screen and (max-width: 767px) {
    display: none !important;
  }
}

.developed__img-wrapper {
  position: relative;

  /*
  // Winter Christmas hat
  &::before {
    position: absolute;
    content: url('@/assets/logo/hat.svg');
    top: -14px;
    left: -4px;
    transform: rotate(1deg);
  }
  */
}

.simple-button {
  background: #1c1c33;
  font-size: 15px;
  line-height: 18px;
  display: flex;
  align-items: center;
  text-align: center;
  letter-spacing: 0.44px;
  color: #fbfaff;
  height: 42px;

  &:hover {
    background: #4a495e;
  }

  &--active {
    background: #4a495e;
  }

  @media screen and (max-width: $hide-header) {
    display: none;
  }
}

.moonpay-button {
  width: 115px;
  height: min-content;
  padding: 0.5rem 0.625rem;
  border: none;

  background-color: transparent;
  border-radius: 0.5rem;
  cursor: pointer;

  color: #e5e4fa;
  font: 500 15px/16px var(--font-family);

  transition: background-color 0.15s ease;
  will-change: background-color, color;

  &:hover,
  &:active,
  &:focus,
  &:focus-visible {
    background-color: #4a495e;
    color: #fbfaff;
  }

  &:focus {
    box-shadow: none;
  }

  @media screen and (max-width: 1400px) {
    display: none;
  }
}

.referral-id {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
